import { ReactNode } from 'react';

interface ActivityItemProps {
  active?: boolean;
  flagged?: boolean;
  children: ReactNode;
}

function ActivityItem({ active, flagged, children }: ActivityItemProps) {
  return (
    <div
      className={`flex items-start gap-[10px] text-[13px] mb-3 leading-[1.5] ${
        flagged ? 'text-[#e0dbd5]' : 'text-[#ccc7c2]'
      }`}
    >
      <div
        className={`w-[6px] h-[6px] rounded-full flex-shrink-0 mt-[5px] ${
          active ? 'bg-[#d43742]' : 'bg-[#4a4440]'
        }`}
      />
      <div>{children}</div>
    </div>
  );
}

function LiveBadge() {
  return (
    <div className="inline-flex items-center gap-2 px-[14px] py-[6px] border border-[#ac1218] text-[11px] font-bold tracking-[0.1em] text-[#d43742] uppercase mb-5">
      <div className="w-[6px] h-[6px] rounded-full bg-[#d43742] animate-pulse" />
      Jessica · Live
    </div>
  );
}

export default function ProblemSection() {
  return (
    <section
      className="py-[110px] px-[60px] border-t border-[#1e1a18] bg-[#060606] max-md:px-6"
      style={{ fontFamily: "'Work Sans', sans-serif" }}
    >
      <div className="max-w-[1080px] mx-auto">
        <span className="block text-[11px] font-bold tracking-[0.22em] text-[#d43742] uppercase mb-7">
          The Problem
        </span>
        <h2
          className="font-black text-[#faf9f7] leading-[0.95] mb-9 tracking-[-0.01em]"
          style={{
            fontFamily: "'Big Shoulders Display', sans-serif",
            fontSize: 'clamp(42px, 6vw, 80px)',
          }}
        >
          The double-book
          <br />
          costs you every time.
        </h2>
        <p className="text-[17px] font-light text-[#e0dbd5] leading-[1.75] max-w-[640px]">
          Two meetings. Same time. You pick the one that feels more urgent and send an apologetic
          conflict message to the other. Maybe you reschedule. Maybe you lose the opportunity
          entirely.
          <br />
          <br />
          <strong className="text-[#e0dbd5] font-semibold">
            That&apos;s not a you problem. That&apos;s a physics problem.
          </strong>{' '}
          You can only be in one place — until now.
        </p>

        {/* Split Scenario */}
        <div
          className="grid grid-cols-1 md:grid-cols-[1fr_2px_1fr] border border-[#2a2422] mt-[70px]"
        >
          {/* Left Panel */}
          <div className="py-[48px] px-[44px] bg-[rgba(12,8,8,0.6)] max-md:p-8">
            <LiveBadge />
            <div
              className="font-black text-[#8a8078] text-[56px] leading-[1] mb-2"
              style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}
            >
              2:00 PM
            </div>
            <div
              className="font-bold text-[#e0dbd5] text-[28px] tracking-[0.04em] mb-6"
              style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}
            >
              Board Prep Call
            </div>
            <div className="h-[1px] bg-[#2a2422] mb-5" />
            <ActivityItem active>
              Summarizing agenda in real time → sent to your phone
            </ActivityItem>
            <ActivityItem>Monitoring for items needing your approval</ActivityItem>
            <ActivityItem>Ready to dial you in when your input is needed</ActivityItem>
          </div>

          {/* Divider */}
          <div
            className="hidden md:block w-[2px]"
            style={{
              background:
                'linear-gradient(to bottom, transparent, #ac1218 30%, #ac1218 70%, transparent)',
            }}
          />
          <div
            className="md:hidden h-[2px] w-full"
            style={{ background: '#ac1218' }}
          />

          {/* Right Panel */}
          <div className="py-[48px] px-[44px] bg-[rgba(8,8,12,0.6)] max-md:p-8">
            <LiveBadge />
            <div
              className="font-black text-[#8a8078] text-[56px] leading-[1] mb-2"
              style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}
            >
              2:00 PM
            </div>
            <div
              className="font-bold text-[#e0dbd5] text-[28px] tracking-[0.04em] mb-6"
              style={{ fontFamily: "'Big Shoulders Display', sans-serif" }}
            >
              Client Check-In
            </div>
            <div className="h-[1px] bg-[#2a2422] mb-5" />
            <ActivityItem active flagged>
              <strong className="text-[#d43742]">Contract amendment raised</strong> — flagged
              immediately
            </ActivityItem>
            <ActivityItem active flagged>
              <strong className="text-[#d43742]">Approval request sent to you</strong> → reply
              yes/no
            </ActivityItem>
            <ActivityItem>Holding the room while you respond</ActivityItem>
          </div>
        </div>
      </div>
    </section>
  );
}
